import React from 'react';
import { MarketHeader } from '../../../components/MarketHeader';
import { CategoryHeader } from './CategoryHeader';
import { FreelanceHeader } from './FreelanceHeader';
import { RoleHeader } from './RoleHeader';
import { CompanyHeader } from './CompanyHeader';
import { WorkModelHeader } from './WorkModelHeader';
import type { WorkModelHubStats } from '../../../types/work-model-hub';
import type { CompanyHubHeaderData } from '../../../utils/seo/build-company-hub-header';
import type { RoleHubStats } from '../../../types/role-hub';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { LOCATION_DESCRIPTIONS } from '../../../utils/constants/location-descriptions';
import type { CategoryDefinition, CategoryHubStats } from '../../../types/category-hub';
import type { FreelanceHubStats } from '../../../types/freelance-hub';

export interface SearchHeaderProps {
  location: string | null;
  /** Locations that currently have a background image (static list plus Lambda-discovered ones). */
  knownLocationBackgrounds?: string[];
  locale: 'en' | 'fi' | 'sv';
  nbHits: number;
  locationTrendsData?: any;
  category?: CategoryDefinition | null;
  categoryHubData?: CategoryHubStats | null;
  isFreelanceHub?: boolean;
  freelanceHubData?: FreelanceHubStats | null;
  /** Set on `/jobs/role/{slug}` pages: renders RoleHeader (H1, salary cross-link, role x city chips). */
  roleHubData?: RoleHubStats | null;
  /** Curated city hub cross-links rendered inside MarketHeader's rail and stat cards. */
  locationHubLinks?: HubCrossLink[];
  /** Set when the SRP is one employer's job list: renders CompanyHeader (the page's H1). */
  companyHub?: CompanyHubHeaderData | null;
  /** Set on `/jobs/remote` and `/jobs/hybrid` pages (and their combinations): renders WorkModelHeader. */
  workModelHubData?: WorkModelHubStats | null;
  className?: string;
}

/**
 * Search header — MarketHeader when a single location filter is active, then CategoryHeader
 * when a category ("field" facet) is active, then FreelanceHeader when the freelance jobType
 * hub is active. Location takes precedence: a combined `/jobs/location/X/category/Y` URL still
 * filters correctly, it just shows the location header rather than stacking static headers.
 * Category is checked before freelance for the same reason — see match-search-index-path.ts's
 * "no freelance+category pretty-path combo" note; if a category page also happens to be
 * jobType-filtered to Freelance via query params, the category framing wins. Otherwise renders
 * nothing — the page's <h1> for that state comes from CustomStatsDisplay (seo_components/CustomStats.tsx),
 * rendered inside SearchSidebar, not from here.
 */
export const SearchHeader: React.FC<SearchHeaderProps> = ({
  location,
  knownLocationBackgrounds,
  locale,
  nbHits,
  locationTrendsData,
  category,
  categoryHubData,
  isFreelanceHub,
  freelanceHubData,
  roleHubData,
  locationHubLinks,
  companyHub,
  workModelHubData,
  className = '',
}) => {
  // Role hub first: a role x city page (`/jobs/location/X/role/Y`) is the role's page scoped to a
  // city, so it gets the role header (H1 "Sairaanhoitajan työpaikat Tampereella") rather than the
  // city's market rail.
  // Work model first: a remote x role or hybrid x city page is the work-model hub narrowed, so it
  // gets the work-model header rather than the role or city one.
  if (workModelHubData) {
    return (
      <section className={`-mx-2 sm:-mx-2 md:-mx-2 lg:-mx-8 -mt-1 sm:-mt-1 md:-mt-2 ${className}`} aria-labelledby="work-model-header-title">
        <WorkModelHeader stats={workModelHubData} locale={locale} />
      </section>
    );
  }
  if (roleHubData) {
    return (
      <section className={`-mx-2 sm:-mx-2 md:-mx-2 lg:-mx-8 -mt-1 sm:-mt-1 md:-mt-2 ${className}`} aria-labelledby="role-header-title">
        <RoleHeader roleHubData={roleHubData} locale={locale} />
      </section>
    );
  }
  if (location) {
    const locationDescription = LOCATION_DESCRIPTIONS[location]?.[locale];
    // No wrapping <section> — MarketHeader renders a rail (desktop, sits beside the results
    // column via the lg:flex row in SrpView) and stat cards (mobile, full width) as siblings;
    // each carries its own aria-labelledby, so an outer landmark here would be redundant.
    return (
      <MarketHeader
        location={location}
        knownLocationBackgrounds={knownLocationBackgrounds}
        nbHits={nbHits}
        description={locationDescription}
        locationTrendsData={locationTrendsData}
        hubLinks={locationHubLinks}
      />
    );
  }

  if (companyHub) {
    return (
      <section className={`-mx-2 sm:-mx-2 md:-mx-2 lg:-mx-8 -mt-1 sm:-mt-1 md:-mt-2 ${className}`} aria-labelledby="company-header-title">
        <CompanyHeader companyHub={companyHub} locale={locale} />
      </section>
    );
  }
  if (category) {
    return (
      <section className={`-mx-2 sm:-mx-2 md:-mx-2 lg:-mx-8 -mt-1 sm:-mt-1 md:-mt-2 ${className}`} aria-labelledby="category-header-title">
        <CategoryHeader
          category={category}
          locale={locale}
          categoryHubData={categoryHubData ?? null}
        />
      </section>
    );
  }

  if (isFreelanceHub) {
    return (
      <section className={`-mx-2 sm:-mx-2 md:-mx-2 lg:-mx-8 -mt-1 sm:-mt-1 md:-mt-2 ${className}`} aria-labelledby="freelance-header-title">
        <FreelanceHeader locale={locale} freelanceHubData={freelanceHubData ?? null} />
      </section>
    );
  }

  return null;
};
